<template>
  <div class="pa-publishing pa-form-shell">
    <form-step
      ref="formStep"
      :site="site"
      :active-audit="activeAudit"
      :schema="schema"
      :sections="sections"
      :readonly="readonly"
      submit-mode="submit"
      expand-all
      @readiness-change="readinessTick += 1"
      @auditUpdated="$emit('audit-updated', $event)"
    >
      <!-- "Add us as an editor on your site" box (design: inside the Publishing card, "Yes" only) -->
      <template #field-publishing_access="{ field, value, setValue, readonly: fieldReadonly }">
        <publishing-access-box
          :value="value"
          :options="field.options || []"
          :readonly="fieldReadonly"
          @input="setValue"
        />
      </template>

      <!-- Website Publishing Agreement box (design: inside the Publishing card, "Yes" only) -->
      <template #field-publishing_agreement_accepted="{ value, setValue, readonly: fieldReadonly }">
        <publishing-agreement-box
          :value="value === true"
          :signed-at="signedAt"
          :site-name="site ? site.name : ''"
          :site-url="site ? site.baseURL : ''"
          :readonly="fieldReadonly"
          @input="setValue"
          @contact="contactOpen = true"
        />
      </template>
    </form-step>

    <!-- "Message your SEO Manager" box (design pa-app.jsx contactCM), from the agreement box link -->
    <contact-manager-modal
      v-if="contactOpen && site && site.id"
      :site-id="site.id"
      topic="Website Publishing Agreement"
      :manager-name="managerName"
      :manager-email="managerEmail"
      @close="contactOpen = false"
    />

    <p v-if="error" class="pa-err">{{ error }}</p>

    <div class="pa-formnav">
      <button type="button" class="pa-btn pa-btn--orange-out pa-btn--pill" :disabled="submitting" @click="$emit('back')">
        <i class="material-icons" aria-hidden="true">arrow_back</i>Back
      </button>
      <button
        v-if="readonly"
        type="button"
        class="pa-btn pa-btn--orange pa-btn--pill"
        @click="$emit('continue')"
      >
        Continue to Google<i class="material-icons" aria-hidden="true">arrow_forward</i>
      </button>
      <button
        v-else
        type="button"
        class="pa-btn pa-btn--orange pa-btn--pill"
        :disabled="!canContinue || submitting"
        @click="submit"
      >
        <span v-if="submitting" class="pa-spin pa-spin--light"></span>
        {{ submitLabel }}<i class="material-icons" aria-hidden="true">arrow_forward</i>
      </button>
    </div>
  </div>
</template>

<script>
import ContactManagerModal from '@/components/SitesSingleView/PopAuthority/ContactManagerModal.vue'
import FormStep from '@/components/SitesSingleView/WhiteGlove/SiteContentAuditWidget/FormStep.vue'
import PublishingAccessBox from './PublishingAccessBox.vue'
import PublishingAgreementBox from './PublishingAgreementBox.vue'
import { makePopAuthorityFormSchema, POP_AUTHORITY_FORM_SECTIONS } from './popAuthorityFormSchema.js'

/**
 * Step 3 "Publishing": last section of the POP Authority form. "Yes, please publish for us" shows
 * the Website Publishing Agreement box; its checkbox is a form answer that the backend turns into
 * the site's signature ("Sign & continue to Google", design pa-setup.jsx PublishingStep). The box's
 * "Contact your SEO Manager" link opens the "Message your SEO Manager" modal.
 * Continuing performs the real form submit (status form_submitted, Asana onboarding note, staff
 * email, site publishing mode) and creates the site's domain-level campaign, exactly like the
 * White Glove audit form.
 */
export default {
  name: 'PopAuthorityPublishingStep',
  components: { ContactManagerModal, FormStep, PublishingAccessBox, PublishingAgreementBox },
  props: {
    site: { type: Object, default: null },
    activeAudit: { type: Object, required: true },
    readonly: { type: Boolean, default: false },
  },
  data () {
    return {
      schema: makePopAuthorityFormSchema(),
      sections: POP_AUTHORITY_FORM_SECTIONS.PUBLISHING,
      readinessTick: 0,
      submitting: false,
      error: '',
      contactOpen: false,
    }
  },
  computed: {
    canContinue () {
      void this.readinessTick
      const form = this.$refs.formStep
      return Boolean(form && form.isFormValid)
    },
    /** When the site signed the current agreement (setup or package purchase), or ''. */
    signedAt () {
      return (this.activeAudit && this.activeAudit.publishingAgreementAcceptedAt) || ''
    },
    managerEmail () {
      return (this.activeAudit && this.activeAudit.campaignManagerEmail) || ''
    },
    managerName () {
      return (this.activeAudit && this.activeAudit.campaignManagerName) || ''
    },
    publishingAnswers () {
      void this.readinessTick
      const form = this.$refs.formStep
      return (form && form.formData && form.formData.step2) || {}
    },
    signsNow () {
      return (
        this.publishingAnswers.need_assistance_publishing === 'yes' &&
        this.publishingAnswers.publishing_agreement_accepted === true &&
        !this.signedAt
      )
    },
    submitLabel () {
      return this.signsNow ? 'Sign & continue to Google' : 'Continue to Google'
    },
  },
  methods: {
    async submit () {
      if (this.submitting || !this.$refs.formStep) return
      this.submitting = true
      this.error = ''
      try {
        const ok = await this.$refs.formStep.submitForm()
        if (ok) {
          this.$emit('done')
        } else {
          // the backend's own reason when it refused the submit (agreement not accepted, password
          // storage not configured), else the generic one
          this.error = this.$refs.formStep.submitError || 'Please fill the required fields marked with *.'
        }
      } catch (e) {
        this.error = 'Could not submit your answers. Please try again.'
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import './setup.scss';

.pa-publishing {
  max-width: 860px;
  color: $pa-teal;
}

.pa-spin--light {
  border-color: rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
}
</style>
